
import { mapGetters, mapActions } from "vuex";
import { isEqual } from "lodash";
import useProductMixin from "~/mixins/useProductMixin";
import googleAnalyticsEvents from "~/mixins/googleAnalyticsEvents";
import facebookPixelEvents from "~/mixins/facebookPixelEvents";
import useEvents from "~/mixins/useEvents";
import { getProductImages, mergeImages, DataURIToBlob } from "~/utils";
import UiButton from "~/components/UI/UiButton.vue";
import UiSpinner from "~/components/UI/UiSpinner.vue";
import ProductMaterialSelectView from "~/components/product/ProductMaterialSelectView.vue";

export default {
  name: "ProductListCarouselCardVertical",
  components: {
    UiButton,
    UiSpinner,
    ProductMaterialSelectView,
  },
  mixins: [
    useProductMixin,
    googleAnalyticsEvents,
    facebookPixelEvents,
    useEvents,
  ],
  props: {
    product: {
      type: Object,
      required: true,
    },
    productIndex: {
      type: Number,
      default: 0,
    },
    statisticItemListName: {
      type: String,
      default: "",
    },
    statisticItemListId: {
      type: String,
      default: "",
    },
    isCardDrawer: {
      type: Boolean,
      default: false,
    },
    ringSizeMaterialCategory: {
      type: Object,
      default: null,
    },
    ringSizeSelectedItem: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      productData: null,
      addingToCart: false,
    };
  },
  computed: {
    ...mapGetters({
      getWishList: "wishlist/getWishList",
    }),
    productSKU() {
      return (
        this.product?.productSKU ||
        this.product?.default_sku ||
        this.product?.selected_config?.configuration_id ||
        this.product?.variant_plp_image?.sku ||
        this.product?.default_config?.configuration_id
      );
    },
    productImage() {
      const images = getProductImages(this.product);
      return images?.[0]?.url || "";
    },
    isInWishlist() {
      return this.getWishList.some((el) =>
        isEqual(
          {
            sku: el.sku,
            engraving: el.engraving,
          },
          { engraving: {}, sku: this.productSKU }
        )
      );
    },
  },
  watch: {
    product: {
      handler(val, oldVal) {
        this.productData = val;

        if (val?.productSKU && val.productSKU !== oldVal?.productSKU) {
          this.productData = {
            ...this.productData,
            default_sku: val.productSKU,
            productSKU: val.productSKU,
          };
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      setWishlist: "wishlist/setWishlist",
    }),
    handleFilter(event) {
      this.$emit("filter", event);
    },
    toggleWishlist() {
      this.onWishlist();
    },
    async onWishlist() {
      if (!this.product.default_sku) {
        return;
      }

      const payload = {
        value: {
          engraving: {},
          images: this.productImage,
          sku: this.productSKU,
        },
      };

      try {
        const wishlistUniqueId = this.$store.getters.getWishListId;
        let wishlist = JSON.parse(JSON.stringify(this.getWishList));
        if (!wishlist) {
          wishlist = [];
        }

        const isExist = wishlist.find((el) =>
          isEqual(
            { sku: el.sku, engraving: el.engraving },
            { engraving: payload.value.engraving, sku: payload.value.sku }
          )
        );

        const wishlistEventData = {
          product_price: this.productPrice,
          product_title: this.productTitle,
          product_id: this.product.id,
          configuration_id: this.productSKU,
          product_configuration_id: this.product?.default_config?.id,
        };

        if (!isExist) {
          wishlist.push({
            ...payload.value,
            slug: this.product.slug,
            title: this.productTitle,
            price: this.productPrice,
            embedded: this.product.embedded,
            translations: this.product.translations || [],
            vendor_model: this.product.vendor_model,
            id: this.product.id,
            collections_ids: this.product.collections_ids || [],
            start_at_cost: this.productPrice,
            product_id: this.product.id,
            configuration_id: this.productSKU,
            product_configuration_id: this.product?.default_config?.id,
          });

          this.gaAddToWishlistEvent(wishlistEventData);
          this.fbAddToWishlistEvent(wishlistEventData);
        } else {
          wishlist = wishlist.filter(
            (el) =>
              !isEqual(
                { sku: el.sku, engraving: el.engraving },
                { engraving: payload.value.engraving, sku: payload.value.sku }
              )
          );
          this.gaRemoveFromWishlistEvent(wishlistEventData);
          this.fbRemoveFromWishlistEvent(wishlistEventData);
        }

        const { data } = await this.$api.wishlist.addItem({
          unique_id: wishlistUniqueId,
          baggage: wishlist,
        });

        const eventPayload = {
          product_id: this.product.id,
          configuration_id: this.productSKU,
          product_sku: this.product.vendor_model,
          product_price: this.productPrice,
          collection_id: this.product.collections_ids?.[0] || "",
          product_configuration_id: this.product?.default_config?.id,
        };

        if (!isExist) {
          this.addToFavoritesEvent(eventPayload);
        } else {
          this.removeFromFavoritesEvent(eventPayload);
        }

        this.setWishlist(data.data.baggage);
      } catch (err) {}
    },
    sendStatistic() {
      this.gaSelectItemEvent(this.product, {
        index: this.productIndex,
        item_list_name: this.statisticItemListName,
        item_list_id: this.statisticItemListId,
      });
    },
    async addToCart() {
      if (this.addingToCart || !this.productSKU) {
        return;
      }
      this.addingToCart = true;
      try {
        const images = getProductImages(this.product);
        const imageIdList =
          images?.filter((item) => item?.id).map((item) => item.id) || [];
        const imageId = imageIdList?.[0];

        let cartImage = null;
        if (!imageId) {
          cartImage = await mergeImages([
            {
              src: this.productImage || "/images/no-image.webp",
            },
          ]);
          cartImage = DataURIToBlob(cartImage);
        }

        const options = {};
        if (this.ringSizeSelectedItem && this.ringSizeMaterialCategory) {
          options.materials = [
            {
              sku: this.ringSizeSelectedItem.sku,
              title:
                this.ringSizeSelectedItem.mixedTranslation?.title ||
                this.ringSizeSelectedItem.title ||
                "",
              material_category_title:
                this.ringSizeMaterialCategory.mixedTranslation?.name || "",
              material_category_id: this.ringSizeMaterialCategory.id,
              material_category_sku: this.ringSizeMaterialCategory.sku,
            },
          ];
        }

        const formData = new FormData();
        formData.append("cart_unique_id", this.$store.getters.getCartId);
        formData.append("sku", this.productSKU);
        formData.append("vendor_model", this.product?.vendor_model || "");
        formData.append("quantity", "1");
        formData.append("options", JSON.stringify(options));

        if (imageId) {
          formData.append("image_id", imageId);
          imageIdList.forEach((id) => {
            formData.append("images_ids[]", id);
          });
        } else {
          formData.append("image", cartImage);
        }

        const { data } = await this.$api.cart.setCartItem(formData);
        await this.$store.dispatch("cart/setCartInfo", data.data);

        if (!this.isCardDrawer) {
          this.$store.commit("general/TOGGLE_CART_DRAWER", true);
        }

        const configInfo = {
          configuration_id: this.productSKU,
          base_cost: this.productPrice,
        };

        this.gaAddToCartEvent(this.product, configInfo);
        this.fbAddToCartEvent(this.product, configInfo);
        this.addToCartEvent({
          product_id: this.product.id,
          configuration_id: this.productSKU,
          product_sku: this.product.vendor_model,
          product_price: this.productPrice,
        });
      } catch (error) {
      } finally {
        this.addingToCart = false;
      }
    },
  },
};
